import { Box, Divider, Grid, IconButton, Typography, useTheme } from '@mui/material';
import { Button, Card, Checkbox, Page, Select, TextField, useDocumentTitle } from '@perry-weather/component-library';
import { Redirect } from 'wouter';
import { Permissions, UserRoles } from '../Constants/Constants';
import { withClickTracking, withRenderTracking } from '../Components/Tracked/withMixpanelTracking';
import { TrackableEvents } from '../Components/Tracked/events';
import { Link, SubCardContainer } from '../Components/Base';
import { SiteSelect } from '../Components/Team/SiteSelect';
import { useGetSSOConfigQuery, useUpdateSSOConfigMutation } from '../Services/API/sso/ssoApi';
import { useUserQuery } from '../Services/API';
import { useState, useEffect } from 'react';
import { openToast } from '../features/toast/toastSlice';
import { useDispatch } from 'react-redux';
import { usePermissionGate } from '../Components/Shared/PermissionGate';
import { ContentCopyOutlined } from '@mui/icons-material';

// This will be the SP Entity ID for any SAML configuration
const DEFAULT_SP_ENTITY_ID = 'https://perryweather.com';

type SSOIdpType = 'Google' | 'Saml2';

type SSOFormState =
  | {
      idpDisplayName: 'Google';
      idpType: 'Google';
      autoProvision: boolean;
      forceSSO: boolean;
      defaultUserGroupSiteId: number | null;
    }
  | {
      idpDisplayName: 'SAML';
      idpType: 'Saml2';
      autoProvision: boolean;
      forceSSO: boolean;
      defaultUserGroupSiteId: number | null;
      properties: {
        spEntityId: string;
        idpEntityId: string;
        metadataUrl: string;
      };
    };

const TrackedPage = withRenderTracking(Page)(TrackableEvents.PageViewed, { page_name: 'Integrations SSO' });
const TrackedButton = withClickTracking(Button)(TrackableEvents.ButtonClicked, {
  button_location: 'Integration SSO',
  button_feature: 'Integration SSO',
  button_type: 'button',
});
const TrackedLink = withClickTracking(Link)(TrackableEvents.ButtonClicked, {
  button_location: 'Integration SSO',
  button_feature: 'Help Center',
  button_type: 'link',
});

const SSO_IDP_OPTIONS: { text: string; value: SSOIdpType }[] = [
  {
    text: 'Google',
    value: 'Google',
  },
  {
    text: 'SAML',
    value: 'Saml2',
  },
];

export function IntegrationsSSO() {
  const { data: user } = useUserQuery();
  const dispatch = useDispatch();
  const theme = useTheme();
  const [formState, setFormState] = useState<SSOFormState>({
    idpDisplayName: 'Google',
    idpType: 'Google',
    autoProvision: false,
    forceSSO: false,
    defaultUserGroupSiteId: null,
  });

  const { data: ssoConfig, isLoading } = useGetSSOConfigQuery(user?.customerId ?? 0, {
    skip: !user?.customerId,
  });

  const [updateSSO, { isSuccess: updateSuccess, isError: updateError }] = useUpdateSSOConfigMutation();
  const samlCallbackUrl = `${process.env.REACT_APP_LOGIN_URL}/federation/${user?.customerId}/Acs`;

  useEffect(() => {
    if (ssoConfig) {
      if (ssoConfig.idpType === 'Google') {
        setFormState({
          idpDisplayName: 'Google',
          idpType: 'Google',
          autoProvision: ssoConfig.autoProvision,
          forceSSO: ssoConfig.forceSSO,
          defaultUserGroupSiteId: ssoConfig.defaultUserGroupSiteId || null,
        });
      }
      if (ssoConfig.idpType === 'Saml2') {
        setFormState({
          idpDisplayName: 'SAML',
          idpType: ssoConfig.idpType,
          autoProvision: ssoConfig.autoProvision,
          forceSSO: ssoConfig.forceSSO,
          defaultUserGroupSiteId: ssoConfig.defaultUserGroupSiteId || null,
          properties: {
            spEntityId: ssoConfig.spEntityId ?? '',
            idpEntityId: ssoConfig.idpEntityId ?? '',
            metadataUrl: ssoConfig.metadataUrl ?? '',
          },
        });
      }
    }
  }, [ssoConfig]);

  useEffect(() => {
    if (updateSuccess) {
      dispatch(openToast({ variant: 'success', header: 'SSO configuration updated successfully' }));
    } else if (updateError) {
      dispatch(openToast({ variant: 'error', header: 'Failed to update SSO configuration' }));
    }
  }, [updateSuccess, updateError]);

  const handleIdpTypeChange = (value: string, text: string) => {
    if (value === 'Google') {
      setFormState({
        idpDisplayName: 'Google',
        idpType: 'Google',
        autoProvision: formState.autoProvision,
        forceSSO: formState.forceSSO,
        defaultUserGroupSiteId: formState.defaultUserGroupSiteId || null,
      });
    }

    if (value === 'Saml2') {
      setFormState({
        idpDisplayName: 'SAML',
        idpType: 'Saml2',
        autoProvision: formState.autoProvision,
        forceSSO: formState.forceSSO,
        defaultUserGroupSiteId: formState.defaultUserGroupSiteId || null,
        properties: {
          spEntityId: DEFAULT_SP_ENTITY_ID,
          idpEntityId: '',
          metadataUrl: '',
        },
      });
    }
  };

  const handleSubmit = async () => {
    if (!user?.customerId) return;

    try {
      await updateSSO({
        customerId: user.customerId,
        IDPScheme: user.customerId.toString(),
        ForceSSO: formState.forceSSO,
        AutoProvision: formState.autoProvision,
        DefaultUserGroupSiteId: formState.autoProvision ? formState.defaultUserGroupSiteId : null,
        IDPDisplayName: formState.idpDisplayName,
        IDPType: formState.idpType,
        ...(formState.idpType === 'Saml2' ? formState.properties : {}),
      });
    } catch (error) {
      console.error('Failed to update SSO config:', error);
    }
  };

  const handleCopyCallbackUrl = () => {
    navigator.clipboard.writeText(samlCallbackUrl);
    dispatch(openToast({ variant: 'success', header: 'Callback URL copied to clipboard' }));
  };

  useDocumentTitle('SSO Integration - Perry Weather');

  const isAdmin = usePermissionGate({
    allowedRoles: [UserRoles.SuperAdmin, UserRoles.Admin, UserRoles.AssistantAdmin],
  });
  const isSSOEntitlementEnabled = usePermissionGate({ allowedPermissions: [Permissions.IntegrationsSSO] });

  if (!isAdmin) {
    return <Redirect to='/Unauthorized' />;
  }
  if (!isSSOEntitlementEnabled) {
    return <Redirect to='/Integrations' />;
  }

  if (isLoading) {
    return <Typography variant='body1'>Loading...</Typography>;
  }

  return (
    <TrackedPage
      docTitle='SSO Configuration'
      title={
        <Typography variant='h3' color='textPrimary' noWrap>
          Single Sign-On
        </Typography>
      }
      subheader={
        <Typography variant='body1' color='textPrimary'>
          Need help? Visit our{' '}
          <TrackedLink
            sx={{ display: 'inline-block' }}
            href='https://support.perryweather.com/hc'
            target='_blank'
            rel='noreferrer'>
            help center
          </TrackedLink>{' '}
          or contact support@perryweather.com
        </Typography>
      }>
      <Grid container spacing={4}>
        <Grid item xs={12}>
          <Card>
            <SubCardContainer
              title='Identity Provider'
              desc={
                <Typography textAlign='left' variant='body2' color='textSecondary'>
                  Select your identity provider
                </Typography>
              }>
              <Select value={formState.idpType} options={SSO_IDP_OPTIONS} onChange={handleIdpTypeChange} />
            </SubCardContainer>
            <Divider style={{ margin: '24px 0' }} />
            <SubCardContainer
              title='Auto-Provision Users'
              desc={
                <Typography textAlign='left' variant='body2' color='textSecondary'>
                  Automatically create user accounts on first sign-in
                </Typography>
              }>
              <Grid container direction='column' gap={2} alignItems='flex-start'>
                <Grid item>
                  <Checkbox
                    sx={{ checkbox: { marginLeft: '12px' } }}
                    checked={formState.autoProvision}
                    label='Enable auto-provisioning'
                    onChange={e =>
                      setFormState({
                        ...formState,
                        autoProvision: e.target.checked,
                      })
                    }
                  />
                </Grid>
                {formState.autoProvision && (
                  <Grid item sx={{ width: '100%' }}>
                    <SiteSelect
                      value={formState.defaultUserGroupSiteId?.toString() ?? ''}
                      customerId={user?.customerId ?? 0}
                      placeholder='Select a group to assign to new users'
                      onSelect={(value, name) =>
                        setFormState({
                          ...formState,
                          defaultUserGroupSiteId: parseInt(value),
                        })
                      }
                    />
                  </Grid>
                )}
              </Grid>
            </SubCardContainer>
            <Divider style={{ margin: '24px 0' }} />
            <SubCardContainer
              title='Require SSO'
              desc={
                <Typography textAlign='left' variant='body2' color='textSecondary'>
                  All users must sign in to Perry Weather through your identity provider
                </Typography>
              }>
              <Grid container direction='column' gap={2} alignItems='flex-start'>
                <Grid item>
                  <Checkbox
                    sx={{ checkbox: { marginLeft: '12px' } }}
                    checked={formState.forceSSO}
                    label='Require SSO for sign-in'
                    onChange={e =>
                      setFormState({
                        ...formState,
                        forceSSO: e.target.checked,
                      })
                    }
                  />
                </Grid>
              </Grid>
            </SubCardContainer>

            {/* Different IDP Types will have different configuration fields here */}
            {formState.idpType === 'Saml2' && (
              <>
                <Divider style={{ margin: '24px 0' }} />
                <SubCardContainer
                  justifyTitle='flex-start'
                  title='SAML Configuration'
                  desc={
                    <Typography textAlign='left' variant='body2' color='textSecondary'>
                      Configure your SAML settings
                    </Typography>
                  }>
                  <Grid container gap={2} direction='column'>
                    <TextField
                      placeholder='SAML Provider Entity ID'
                      value={formState.properties?.idpEntityId ?? ''}
                      helperText='The Entity ID of the SAML provider (for example, a URL from Okta or Entra)'
                      onChange={e =>
                        setFormState({
                          ...formState,
                          properties: { ...formState.properties, idpEntityId: e.target.value },
                        })
                      }
                    />
                    <TextField
                      placeholder='Metadata URL'
                      value={formState.properties?.metadataUrl ?? ''}
                      helperText='The URL of the SAML provider metadata'
                      onChange={e =>
                        setFormState({
                          ...formState,
                          properties: { ...formState.properties, metadataUrl: e.target.value },
                        })
                      }
                    />
                    <TextField
                      value={samlCallbackUrl}
                      helperText="The callback URL. Copy and paste this into your SAML provider's Reply URL."
                      endAdornment={
                        <IconButton onClick={handleCopyCallbackUrl}>
                          <ContentCopyOutlined sx={{ color: theme.palette.text.primary }} />
                        </IconButton>
                      }
                      readonly
                    />
                  </Grid>
                </SubCardContainer>
              </>
            )}

            <Box marginTop='48px' display='flex' justifyContent='flex-end'>
              <TrackedButton onClick={handleSubmit} disabled={isLoading}>
                <Typography variant='body1'>Save Changes</Typography>
              </TrackedButton>
            </Box>
          </Card>
        </Grid>
      </Grid>
    </TrackedPage>
  );
}

export default IntegrationsSSO;
